<template>
  <div>
    <Head title="Cajas Registradoras" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <h1 class="text-3xl font-bold">Cajas Registradoras</h1>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div class="flex items-center gap-3">
        <Link
          class="btn-blue flex items-center gap-2"
          href="/cash-registers/create"
        >
          <i class="fa fa-plus"></i>
          <span>Crear Caja</span>
        </Link>

        <Link
          class="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors"
          href="/cash-registers/daily-report"
        >
          <i class="fa fa-chart-pie"></i>
          <span>Reporte Diario Consolidado</span>
        </Link>

        <Link
          class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors"
          style="background-color: #00b074;"
          href="/cash-closures"
        >
          <i class="fa fa-history"></i>
          <span>Historial de Cierres y Arqueos</span>
        </Link>

        <Link
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors"
          href="/cash-transactions"
        >
          <i class="fa fa-list"></i>
          <span>Movimientos de Caja</span>
        </Link>

        <button
          type="button"
          @click="showCashAdvanceModal = true"
          class="p-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg shadow-sm flex items-center justify-center transition-colors cursor-pointer"
          title="Avance de Efectivo en Pesos (COP)"
        >
          <i class="fa fa-exchange-alt"></i>
        </button>

        <button
          type="button"
          @click="showWithdrawalModal = true"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
        >
          <i class="fa fa-hand-holding-usd"></i>
          <span>Retiro de Efectivo</span>
        </button>
      </div>

      <!-- Filtro por Sucursal / Tienda para Administradores -->
      <div v-if="organizations && organizations.length > 1" class="flex items-center gap-2">
        <label class="text-xs font-bold text-gray-600 uppercase"><i class="fa fa-store text-slate-400 mr-1"></i>Sucursal:</label>
        <select
          v-model="selectedOrg"
          class="text-xs font-semibold rounded-lg border-gray-300 py-2 px-3 bg-white shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
        >
          <option value="">Todas las Sucursales</option>
          <option v-for="org in organizations" :key="org.id" :value="org.id">
            {{ org.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Cash Registers Table -->
    <div class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-center bg-gray-50">
            <th class="p-3 font-semibold">Nombre</th>
            <th class="p-3 font-semibold">Estado</th>
            <th class="p-3 font-semibold">Saldo Inicial</th>
            <th class="p-3 font-semibold">Saldo Actual</th>
            <th class="p-3 font-semibold">Abierta por</th>
            <th class="p-3 font-semibold">Fecha Apertura</th>
            <th class="p-3 font-semibold">Acciones</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="cashRegister in cashRegisters.data"
            :key="cashRegister.id"
            class="border-t border-gray-100 hover:bg-gray-50 transition-colors text-center"
          >
            <td class="p-3">
              <Link
                :href="`/cash-registers/${cashRegister.id}`"
                class="text-gray-800 font-bold hover:text-blue-600 block"
              >
                {{ cashRegister.name || ('Caja #' + cashRegister.id) }}
              </Link>
              <span v-if="cashRegister.organization" class="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                <i class="fa fa-store text-slate-400"></i> {{ cashRegister.organization.name }}
              </span>
            </td>

            <td class="p-3">
              <span
                :class="getStatusClass(cashRegister.status)"
                class="px-2 py-1 rounded-full text-xs font-medium"
              >
                {{ getStatusText(cashRegister.status) }}
              </span>
            </td>

            <td class="p-3 text-gray-700 text-xs text-center">
              <div class="font-bold text-gray-900">${{ formatCurrency(cashRegister.opening_balance) }} USD</div>
              <div v-if="cashRegister.opening_balance_bs > 0" class="text-gray-500 text-[11px]">Bs. {{ formatCurrency(cashRegister.opening_balance_bs) }}</div>
              <div v-if="cashRegister.opening_balance_cop > 0" class="text-teal-700 text-[11px] font-semibold">COP {{ formatCurrency(cashRegister.opening_balance_cop) }}</div>
            </td>

            <td class="p-3 text-gray-700 text-xs text-center">
              <div class="font-bold text-gray-900">${{ formatCurrency(cashRegister.current_balance) }} USD</div>
              <div v-if="cashRegister.current_balance_bs > 0" class="text-gray-500 text-[11px]">Bs. {{ formatCurrency(cashRegister.current_balance_bs) }}</div>
              <div v-if="cashRegister.current_balance_cop > 0" class="text-teal-700 text-[11px] font-semibold">COP {{ formatCurrency(cashRegister.current_balance_cop) }}</div>
            </td>

            <td class="p-3 text-gray-800 text-xs font-semibold text-center">
              {{ cashRegister.openedBy?.name || (cashRegister.openedBy ? (cashRegister.openedBy.first_name + ' ' + (cashRegister.openedBy.last_name || '')) : (cashRegister.opened_by?.name || 'N/A')) }}
            </td>

            <td class="p-3 text-gray-600">
              {{ cashRegister.opened_at ? formatDate(cashRegister.opened_at) : 'N/A' }}
            </td>

            <td class="p-3">
              <div class="flex items-center justify-center space-x-2">
                <Link
                  :href="`/cash-registers/${cashRegister.id}`"
                  class="p-2 text-blue-600 hover:text-blue-900 rounded hover:bg-blue-50 transition-colors"
                  title="Ver sumatoria y detalles de la caja"
                >
                  <i class="fa fa-eye"></i>
                </Link>

                <Link
                  v-if="cashRegister.status === 'closed'"
                  :href="`/cash-registers/${cashRegister.id}/edit`"
                  class="p-2 text-yellow-600 hover:text-yellow-900 rounded hover:bg-yellow-50 transition-colors"
                  title="Editar"
                >
                  <i class="fa fa-edit"></i>
                </Link>

                <Link
                  v-if="cashRegister.status === 'closed'"
                  :href="`/cash-registers/${cashRegister.id}/open`"
                  class="p-2 text-green-600 hover:text-green-900 rounded hover:bg-green-50 transition-colors"
                  title="Abrir caja"
                >
                  <i class="fa fa-play"></i>
                </Link>

                <Link
                  v-if="cashRegister.status === 'open'"
                  :href="`/cash-registers/${cashRegister.id}/close`"
                  class="p-2 text-red-600 hover:text-red-900 rounded hover:bg-red-50 transition-colors"
                  title="Cerrar caja"
                >
                  <i class="fa fa-stop"></i>
                </Link>

                <button
                  v-if="cashRegister.status === 'closed'"
                  @click="deleteCashRegister(cashRegister)"
                  class="p-2 text-red-600 hover:text-red-900 rounded hover:bg-red-50 transition-colors"
                  title="Eliminar"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!cashRegisters.data || cashRegisters.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="7">No hay cajas registradoras.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      v-if="cashRegisters?.links || cashRegisters?.meta?.links"
      :links="cashRegisters.links || cashRegisters.meta.links"
      class="mt-6"
    />

    <!-- Modal de Retiro de Efectivo -->
    <withdrawal-modal
      :show="showWithdrawalModal"
      @close="showWithdrawalModal = false"
      :is-admin="$page.props.auth?.user?.role === 'admin'"
    />

    <!-- Modal de Avance de Efectivo en Pesos (COP) -->
    <cash-advance-modal
      :show="showCashAdvanceModal"
      :default-rate="$page.props.cop_bs_rate || $page.props.cop_change || 2.89"
      @close="showCashAdvanceModal = false"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import WithdrawalModal from '@/Shared/WithdrawalModal.vue'
import CashAdvanceModal from '@/Shared/CashAdvanceModal.vue'

export default {
  name: 'CashRegistersIndex',

  components: {
    Head,
    Link,
    Pagination,
    WithdrawalModal,
    CashAdvanceModal,
  },

  layout: Layout,

  props: {
    cashRegisters: {
      type: Object,
      required: true
    },
    organizations: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      showWithdrawalModal: false,
      showCashAdvanceModal: false,
      selectedOrg: (this.filters && this.filters.organization_id) || '',
    }
  },

  watch: {
    selectedOrg(val) {
      this.$inertia.get('/cash-registers', { organization_id: val }, { preserveState: true, replace: true })
    }
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    getStatusClass(status) {
      return {
        'open': 'bg-green-100 text-green-800',
        'closed': 'bg-red-100 text-red-800',
      }[status] || 'bg-gray-100 text-gray-800'
    },

    getStatusText(status) {
      return {
        'open': 'Abierta',
        'closed': 'Cerrada',
      }[status] || 'Desconocido'
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'USD'
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    openCashRegister(cashRegister) {
      // Mostrar modal/formulario inline para declarar saldos iniciales
      const usdAmount = prompt('Saldo inicial en USD:', '0');
      if (usdAmount === null) return; // Cancelado

      const bsAmount = prompt('Saldo inicial en Bolívares (BS):', '0');
      if (bsAmount === null) return; // Cancelado

      const copAmount = prompt('Saldo inicial en Pesos Colombianos (COP):', '0');
      if (copAmount === null) return; // Cancelado

      // Validar que sean números
      const usd = parseFloat(usdAmount) || 0;
      const bs = parseFloat(bsAmount) || 0;
      const cop = parseFloat(copAmount) || 0;

      if (confirm(`¿Confirmar apertura de caja con:\n- USD: ${usd}\n- BS: ${bs}\n- COP: ${cop}`)) {
        this.$inertia.post(`/cash-registers/${cashRegister.id}/open`, {
          opening_balance_usd: usd,
          opening_balance_bs: bs,
          opening_balance_cop: cop,
        }, {
          onSuccess: () => {
            this.$inertia.visit(`/cash-registers/${cashRegister.id}`)
          }
        })
      }
    },

    closeCashRegister(cashRegister) {
      if (confirm('¿Estás seguro de que quieres cerrar esta caja? Se generará un reporte de cierre con todos los movimientos.')) {
        // Mostrar modal/formulario inline para declarar saldos reales
        const usdAmount = prompt('Saldo real en USD:', cashRegister.current_balance.toString());
        if (usdAmount === null) return; // Cancelado

        const bsAmount = prompt('Saldo real en Bolívares (BS):', cashRegister.current_balance_bs.toString());
        if (bsAmount === null) return; // Cancelado

        const copAmount = prompt('Saldo real en Pesos Colombianos (COP):', cashRegister.current_balance_cop.toString());
        if (copAmount === null) return; // Cancelado

        const notes = prompt('Notas (opcional):', '');

        // Validar que sean números
        const usd = parseFloat(usdAmount) || 0;
        const bs = parseFloat(bsAmount) || 0;
        const cop = parseFloat(copAmount) || 0;

        if (confirm(`¿Confirmar cierre de caja con:\n- USD: ${usd}\n- BS: ${bs}\n- COP: ${cop}\n- Notas: ${notes || 'Sin notas'}`)) {
          this.$inertia.post(`/cash-registers/${cashRegister.id}/close`, {
            closing_balance_usd: usd,
            closing_balance_bs: bs,
            closing_balance_cop: cop,
            notes: notes || null,
          }, {
            onSuccess: () => {
              this.$inertia.visit('/cash-registers')
            }
          })
        }
      }
    },

    deleteCashRegister(cashRegister) {
      if (confirm('¿Estás seguro de que quieres eliminar esta caja?')) {
        this.$inertia.delete(`/cash-registers/${cashRegister.id}`)
      }
    }
  }
}
</script>